import type { ReactNode } from 'react';
import { AlertTriangle, CheckCircle2, Info, XCircle } from 'lucide-react';
import { cn } from '@/lib/cn';

export type AlertTone = 'info' | 'success' | 'warning' | 'danger';

const TONES: Record<AlertTone, { box: string; icon: ReactNode }> = {
  info: { box: 'border-info/40 bg-info-soft text-info', icon: <Info className="h-4 w-4" /> },
  success: {
    box: 'border-success/40 bg-success-soft text-success',
    icon: <CheckCircle2 className="h-4 w-4" />,
  },
  warning: {
    box: 'border-warning/40 bg-warning-soft text-warning',
    icon: <AlertTriangle className="h-4 w-4" />,
  },
  danger: { box: 'border-danger/40 bg-danger-soft text-danger', icon: <XCircle className="h-4 w-4" /> },
};

export interface AlertProps {
  tone?: AlertTone;
  title?: ReactNode;
  children?: ReactNode;
  className?: string;
  action?: ReactNode;
}

export function Alert({ tone = 'info', title, children, className, action }: AlertProps) {
  return (
    <div
      role={tone === 'danger' ? 'alert' : 'status'}
      className={cn('flex gap-3 rounded-lg border px-3.5 py-3 text-sm', TONES[tone].box, className)}
    >
      <span className="mt-0.5 shrink-0" aria-hidden>
        {TONES[tone].icon}
      </span>
      <div className="min-w-0 flex-1">
        {title && <p className="font-semibold">{title}</p>}
        {children && <div className={cn('text-sm', title && 'mt-0.5 opacity-90')}>{children}</div>}
      </div>
      {action && <div className="shrink-0 self-center">{action}</div>}
    </div>
  );
}
